import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, ArrowUpRight, Download } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 6;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [detail, setDetail] = useState(null);
  const [tools, setTools] = useState([]);
  const { clear } = useCart();
  const attempts = useRef(0);

  const downloadToolFile = async (t) => {
    try {
      const res = await axios.get(`${API}/tools/${t.slug}/download?session_id=${sessionId}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `${t.name}.${t.file_ext || "zip"}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Téléchargement impossible."); }
  };

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        setDetail(data);
        if (data.payment_status === "paid") {
          setState("paid");
          clear();
          const toolLks = (data.lookup_keys || [data.lookup_key]).filter((lk) => lk?.startsWith("tool_"));
          if (toolLks.length) {
            axios.get(`${API}/tools`).then(({ data: all }) => {
              setTools((all || []).filter((x) => toolLks.includes(x.lookup_key)));
            }).catch(() => {});
          }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) {
          setState("pending");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) {
          setState("error");
          return;
        }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <section className="relative min-h-[85vh] bg-ink-900 text-paper grain flex items-center" data-testid="payment-success">
      <div className="absolute right-0 top-0 w-[40vw] h-[40vw] rounded-full bg-gold/10 blur-[120px]" />
      <div className="relative z-10 mx-auto max-w-2xl px-5 md:px-10 text-center pt-32 pb-20">
        {state === "checking" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Loader2 size={54} className="text-gold mx-auto animate-spin" />
            <h1 className="font-display text-4xl md:text-5xl font-semibold mt-8">Confirmation en cours…</h1>
            <p className="text-paper/60 mt-4">Nous validons votre paiement auprès de Stripe.</p>
          </motion.div>
        )}

        {state === "paid" && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <CheckCircle2 size={64} className="text-gold mx-auto" />
            <span className="label-mono text-gold block mt-6">Paiement confirmé</span>
            <h1 className="font-display text-4xl md:text-6xl font-semibold mt-4 leading-[0.95]">{tools.length > 0 ? (tools.length > 1 ? "Vos outils sont prêts !" : "Votre outil est prêt !") : "Bienvenue dans la formation !"}</h1>
            {(detail?.product_names?.length > 0 || detail?.product_name) && (
              <p className="text-paper/70 mt-6 text-lg">
                Votre commande <span className="text-paper font-semibold">« {(detail?.product_names?.length ? detail.product_names.join(" + ") : detail.product_name)} »</span> est confirmée.
                {detail.amount ? ` (${(detail.amount / 100).toFixed(0)} ${String(detail.currency).toUpperCase()})` : ""}
              </p>
            )}
            {tools.length > 0 ? (
              <>
                <p className="text-paper/50 mt-3">Téléchargez vos fichiers immédiatement, sans créer de compte. Conservez l'adresse de cette page pour y revenir plus tard.</p>
                <div className="flex flex-col gap-3 mt-10 max-w-md mx-auto">
                  {tools.map((t) => (
                    <button key={t.slug} onClick={() => downloadToolFile(t)} data-testid={`success-download-${t.slug}`} className="inline-flex items-center justify-center gap-2 rounded-full bg-gold text-ink px-7 py-4 label-mono hover:bg-paper transition-colors">
                      Télécharger — {t.name} <Download size={15} />
                    </button>
                  ))}
                  <Link to="/outils-apps" className="inline-flex items-center justify-center gap-2 rounded-full border border-paper/30 px-7 py-4 label-mono hover:bg-paper hover:text-ink transition-colors">
                    Découvrir d'autres outils <ArrowUpRight size={15} />
                  </Link>
                </div>
              </>
            ) : (
              <>
                <p className="text-paper/50 mt-3">Créez un compte ou connectez-vous avec l'email utilisé lors du paiement pour retrouver votre formation dans Mon Espace.</p>
                <div className="flex flex-wrap gap-3 justify-center mt-10">
                  <Link to="/mon-espace" data-testid="success-back-formation" className="inline-flex items-center gap-2 rounded-full bg-gold text-ink px-7 py-4 label-mono hover:bg-paper transition-colors">
                    Accéder à Mon Espace <ArrowUpRight size={15} />
                  </Link>
                  <Link to="/" className="inline-flex items-center gap-2 rounded-full border border-paper/30 px-7 py-4 label-mono hover:bg-paper hover:text-ink transition-colors">
                    Retour à l'accueil
                  </Link>
                </div>
              </>
            )}
          </motion.div>
        )}

        {state === "pending" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Loader2 size={54} className="text-gold mx-auto animate-spin" />
            <h1 className="font-display text-4xl font-semibold mt-8">Paiement en traitement</h1>
            <p className="text-paper/60 mt-4">Votre paiement est en cours de validation. Vous recevrez un email de confirmation sous peu.</p>
            <Link to="/" className="inline-flex items-center gap-2 rounded-full bg-gold text-ink px-7 py-4 label-mono hover:bg-paper transition-colors mt-10">Retour à l'accueil</Link>
          </motion.div>
        )}

        {state === "error" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <XCircle size={64} className="text-paper/70 mx-auto" />
            <h1 className="font-display text-4xl font-semibold mt-8">Paiement non confirmé</h1>
            <p className="text-paper/60 mt-4">Nous n'avons pas pu confirmer ce paiement. Aucun montant n'a été débité en cas d'échec.</p>
            <div className="flex flex-wrap gap-3 justify-center mt-10">
              <Link to="/formation" className="inline-flex items-center gap-2 rounded-full bg-gold text-ink px-7 py-4 label-mono hover:bg-paper transition-colors">Réessayer</Link>
              <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-paper/30 px-7 py-4 label-mono hover:bg-paper hover:text-ink transition-colors">Nous contacter</Link>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
